// Per spec/components.md §10. Sub-section-style state header + per-group
// rows. The group label itself is the link (no separate "Join" affordance
// after P-037.17 D-117). Group rows are tight-stacked under the state
// header with no border rules between them — the SubSectionHeader's
// padded divider above carries the per-state grouping signal.
//
// **P-037.15 (D-115)** — state header switched from `StateBar` to
// `SubSectionHeader` per Banks's audit.
// **P-037.17 (D-117)** — drop the standalone "Join →" link; the group
// label is now the anchor. Group link renders at 14 px Helvetica bold
// gold `#c69b34` (matches the system-wide CTA-link color).
// **P-037.23 (D-131)** — accept `groups` as an explicit prop instead of
// reading `state.whatsappGroups`. The chapter-whatsapp Block now sources
// per-state groups from its own `inputs.groupsByState[id]` (per-Email-
// editable per Banks's Option C), so the registry's whatsappGroups
// serves only as the schema-default seed. WhatsAppRow stays Component-
// pure: state supplies the header (name + flag), `groups` supplies the
// rendered list.
//
// **P-037.30 §item C** — restore the 16 px gap below the state name+flag
// SubSectionHeader. At P-037.29 D-137 §item 2, `SubSectionHeader.
// PAD_BOTTOM` dropped from 16 → 0 in lockstep with the new system-level
// inter-Block padding spacer. The system spacer fires BETWEEN Blocks
// (correct) — but the SubSectionHeader inside whatsapp-row is followed
// by group-link rows in the SAME table, so the system spacer doesn't
// fire there and the gap collapsed. Local fix: the FIRST group row
// now carries a 16 px top-pad (subsequent rows keep the existing 4 px
// GROUP_GAP inter-group rhythm). WhatsAppRow is consumed only by
// chapter-whatsapp, so this local pad doesn't ripple to other Blocks
// that use SubSectionHeader (events-list, big3-signature-events, etc.).

import { html } from '../render'
import { tokens } from '../tokens'
import { link } from '../primitives/link'
import { subSectionHeader } from './sub-section-header'
import { HtmlString } from '../types'
import { type State, type WhatsAppGroup } from '../registry/states'

/** Only the registry fields WhatsAppRow renders directly — name (the
 *  SubSectionHeader text) + flagImg (the 40 × 24 flag asset). The
 *  registry's `whatsappGroups` field is no longer read here (P-037.23 /
 *  D-131); narrowing the prop type advertises that. Callers can still
 *  pass a full `State` — TS structural narrowing accepts the wider shape. */
type WhatsAppRowState = Pick<State, 'name' | 'flagImg'>

export interface WhatsAppRowProps {
  state: WhatsAppRowState
  /** The per-state WhatsApp groups to render. Sourced from chapter-
   *  whatsapp's `inputs.groupsByState[id]` (P-037.23 / D-131 — per-Email-
   *  editable). The state registry's `whatsappGroups` is the schema's
   *  default seed; WhatsAppRow no longer reads it directly. */
  groups: ReadonlyArray<WhatsAppGroup>
  /** Template-level link color from `RenderContext.linkColor` (P-037.18
   *  D-118). Used for the per-group label anchor. Defaults to
   *  `tokens.color['link-default']`. */
  linkColor?: string
}

const GROUP_LINK_SIZE_PX = 14
const GROUP_GAP = tokens.space[1]
// P-037.30 §item C — top-pad on the FIRST group row, restoring the gap
// below the SubSectionHeader that collapsed when `SubSectionHeader.
// PAD_BOTTOM` dropped from 16 → 0 at P-037.29 D-137 §item 2. Matches
// the original PAD_BOTTOM value (16 px = tokens.space[4]).
const FIRST_GROUP_TOP_PAD = tokens.space[4]

export function whatsAppRow({ state, groups, linkColor }: WhatsAppRowProps): HtmlString {
  // P-037.18 D-118: anchor color is template-level. Falls back to
  // `tokens.color['link-default']` when the caller (e.g., gallery
  // synthetic envelope) doesn't thread a linkColor.
  const anchorColor = linkColor ?? tokens.color['link-default']
  // P-037.17 D-117: padX=0 so the flag+name aligns flush-left with the
  // parent grid cell's edge (chapter-whatsapp embeds whatsAppRow inside
  // a 190 px cell; the default 30 px L/R padding would push the flag
  // toward visual center).
  const headerHtml = subSectionHeader({
    heading: state.name,
    flagImageUrl: state.flagImg,
    padX: 0,
  })
  // Wrap each anchor in a styled `<p>` (margin: 0) so font-size cascades to
  // the link. The link primitive sets font-family + color + weight + text-
  // decoration; font-size flows from the wrapping cell's inline style.
  const groupRows = groups.map((group, index) => {
    const anchor = link({
      text: group.label,
      href: group.url,
      color: anchorColor,
      weight: tokens.type.weight.bold,
    })
    // Branch the template literal on the index to set the appropriate
    // top-pad: row 0 carries the 16 px gap below the SubSectionHeader
    // (P-037.30 §item C); rows 1+ carry the 4 px inter-group rhythm.
    // prettier-ignore
    return index === 0
      ? html`<tr><td style="font-family: ${tokens.type.fontFamily}; font-size: ${GROUP_LINK_SIZE_PX}px; line-height: 1.5; padding-top: ${FIRST_GROUP_TOP_PAD}px;">${anchor}</td></tr>`
      : html`<tr><td style="font-family: ${tokens.type.fontFamily}; font-size: ${GROUP_LINK_SIZE_PX}px; line-height: 1.5; padding-top: ${GROUP_GAP}px;">${anchor}</td></tr>`
  })
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td>${headerHtml}</td></tr>${groupRows}</table>`
}
